import type { BackgroundJobView } from '../../types/job'

export interface JobTarget {
  to: string
}

/**
 * `sourceType -> frontend route`, table-driven (IB-27 S2 design §4.5/§6 RAID-1) — a generic
 * job-metadata lookup, the SAME pattern as `KIND_LABEL` (`types/job.ts`), not a jurisdiction-branch
 * invariant violation (CLAUDE.md §2.2 does not apply: no `if (state == …)`, just a lookup table
 * over a stable dimension every future consumer already publishes).
 *
 * The server's `BackgroundJobDetailView.deepLinkUrl` (`"/" + sourceType + "/" + sourceId"`) does
 * NOT match any real React route, so resolution is a frontend presentation concern (design §2.2) —
 * this map is built from `(sourceType, sourceId)` on the LIST row directly; the detail endpoint is
 * never fetched just to decide a deep link.
 *
 * `sourceType` casing is NOT uniform across today's submitters — verified by reading every
 * `new SourceRef(...)` call site under `backend/app/src/main/java` as of this story:
 *   - `"ingestion_run"` (`IngestionRunService`) -> the Data & bulk-ops screen's "Recent activity".
 *   - `"BulkOperation"` (`BulkOperationService`), `"PurgeRequest"` (`PurgeService`) -> the Bulk
 *     operations list (no per-row detail route exists yet for a single bulk op/purge request, so
 *     this links to the list, not a specific row — named, not silently generic).
 *   - `"notification"` (`NotificationFanoutService`) -> the caller's own Notifications list.
 * Deliberately unmapped (falls through to `null` — NO link affordance, never a dead CTA):
 *   - `"form_instance"` (`FORM_RENDER`) — no frontend route for a single `FormInstance` exists yet.
 *   - `"DossierGenerate"`/`"DossierFinalize"` (`DossierAssemblyExecutor`) — the Dossier route is
 *     nested under its owning Case (`/cases/:caseId/dossier`) and the job row only carries the
 *     `dossierId`, not the `caseId`; building a safe link would need an extra fetch this story's
 *     design deliberately avoids (§2.2 "we do not fetch detail just for the deep link").
 *   - `"notify_digest_recipient"` (`NOTIFICATION_DIGEST` sweep) — a system-initiated job
 *     (`initiatedBy = SystemActor.ID`) that can never appear in a personal `initiated_by=me` tray.
 *   - any future/unrecognized `sourceType` (e.g. a not-yet-wired `CAMPAIGN_*`/`EXPORT`/`RECOMPUTE`/
 *     `BULK_AI_RUN` submitter) — a real gap named here, not guessed at.
 */
const SOURCE_TYPE_ROUTE: Record<string, string> = {
  ingestion_run: '/data/import',
  BulkOperation: '/data/bulk',
  PurgeRequest: '/data/bulk',
  notification: '/my-work/notifications',
}

export function resolveJobTarget(job: Pick<BackgroundJobView, 'sourceType'>): JobTarget | null {
  const to = SOURCE_TYPE_ROUTE[job.sourceType]
  return to ? { to } : null
}
